import React, {useState} from 'react';
import ErrorFormHelper from "../../../utils/ErrorFormHelper";
import {ReactComponent as RemoveSVG} from "../../../../images/icons/remove.svg";
import ErrorFormView from "../../Common/ErrorFormView";
import {useTranslation, withTranslation} from "react-i18next";
import {ApiError} from "../../../services/Api/types";
import handleException from "../../../services/Api/handleException";
import {useCart} from "../../../services/Cart/cartService";
import {connect, DispatchProp} from "react-redux";
import {RootState} from "../../../../reducers";
import {getCart} from "../../../services/Cart/reducer";
import {ReactComponent as BogoIco} from "../../../../images/icons/bogo-outline.svg";
import Money from "../../Common/Money";
import {useVoucherProducts} from "../../../hooks/useVoucherProducts";
import {
    getAvailabilities,
    getCategories,
    getItems,
    getMenus,
    getPromotions
} from "../../../services/Menu/reducer";
import VoucherProductsModal from "../../Modal/VoucherProductsModal";
import {getSession} from "../../../services/Session/reducer";
import {getVouchers} from "../../../services/User/reducer";

interface OwnProps {
    onHide: () => void;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

const DrawerVouchersCoupon = ({cart, onHide, items, menus, categories, promotions, session, availabilities, vouchers}: Props) => {
    const {t} = useTranslation();
    const [couponValue, setCouponValue] = useState(cart.coupon);
    const cartService = useCart();
    const [couponLoading, setCouponLoading] = useState(false);
    const [errors, setErrors] = useState<ApiError[]>([]);
    const couponPromotion = couponValue ? cart.promotions.filter((promotion) => promotion.metadata && promotion.metadata.toLocaleUpperCase().includes(`CODE_${couponValue.toLocaleUpperCase()}`))[0] : undefined;
    const couponError = !(errors.length > 0 || (couponPromotion === undefined || !cart.coupon || (cart.discount_money && cart.discount_money.amount > 0)));
    const [showVoucherProductsModal, setShowVoucherProductsModal] = useState(false);

    const handleAddCoupon = async () => {
        setCouponLoading(true);
        try {
            const response = await cartService.updateOrderCoupon({
                code: couponValue
            });
            setErrors([]);

            const couponFromResponse = response.promotions.find((promotion: any) => promotion.metadata === `CODE_${couponValue.toLocaleUpperCase()}`);
            await handlePromotionChange(couponFromResponse.promotion_id);

            if (response.promotions.some((promotion: any) => promotion.type === "COUPON")) {
                onHide();
            }
        } catch (error: any) {
            const errors = handleException(error);
            handleSetCouponErrors(errors);
        }
        setCouponLoading(false);
    }

    const handleChangeCoupon = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCouponValue(event.target.value)
    }

    const handleDeleteCoupon = async () => {
        setCouponLoading(true)
        try {
            await cartService.updateOrderCoupon({
                code: ""
            });
            setCouponValue("");
            setErrors([])
        } catch (error: any) {
            const errors = handleException(error);
            handleSetCouponErrors(errors);
        }
        setCouponLoading(false)
    }

    const handleSetCouponErrors = (errors: ApiError[]) => {
        setErrors(errors.map(error => {
            return {
                ...error,
                field: "code"
            }
        }));
    }

    const drawCouponInfo = () => {
        if(couponPromotion) {
            const metadata = couponPromotion.metadata.replace("CODE_", "");

            if (!cart.coupon_id && cart.coupon && metadata && metadata === cart.coupon) {
                return (
                    <>
                        <p className="mb-0 text-success fw-normal">{couponPromotion.name}</p>
                        <small className="text-muted text-start fw-normal">{metadata}</small>
                    </>
                )
            }

            const promotionTitle = metadata ? `${couponPromotion.name} / ${metadata}` : couponPromotion.name;

            return (
                <>
                    <p className="mb-0 text-success fw-normal">{couponPromotion.value_money.amount ? "-" : ""} <Money value={couponPromotion.value_money.amount } currency={couponPromotion.value_money.currency}/></p>
                    <small className="text-muted text-start fw-normal">{promotionTitle}</small>
                </>
            )
        }

        return (
            <p className="mb-0 text-success fw-normal">{cart.coupon}</p>
        );
    }

    const onRemovePromotion = async () => {
        await handleDeleteCoupon();
    }

    const {handlePromotionChange, promotionItems, findMenuForPromotionItem, handleRemovePromotion } = useVoucherProducts({
        menuItems: items,
        handleOpenItemsModal: () => setShowVoucherProductsModal(true),
        menus,
        categories,
        cart,
        userVouchers: vouchers || [],
        onRemovePromotion
    });

	return (
        <div className="mb-4">
            <h5 className="mb-3">{t("modules.navbar.field.coupon_code.title")}</h5>
            {cart.coupon ? (
                <>
                    <div className={"btn btn-default promotion-coupon-box btn-nav btn-lg"}>
                        <div className="d-flex align-items-center">
                            <BogoIco className="text-success"/>
                            <div className="d-flex flex-column ms-2 align-items-start">
                                {drawCouponInfo()}
                            </div>
                        </div>
                        <button className={"btn btn-outline-danger btn-icon"} onClick={handleDeleteCoupon} aria-label={t("common.action.remove")}>
                            <RemoveSVG aria-hidden="true" />
                        </button>
                    </div>
                    <ErrorFormView errors={errors} field="code"/>
                </>
            ) : (
                <>
                    <div
                        className={"form-group mb-3" + ErrorFormHelper(errors, "code") + (couponError ? " is-invalid" : "")}>
                        <input type="text" onChange={handleChangeCoupon} value={couponValue}
                               className={"form-control" + ErrorFormHelper(errors, "code") + (couponError ? " is-invalid" : "")}
                               placeholder={t("modules.navbar.field.coupon_code.placeholder")}
                               aria-describedby="cart-coupon"/>
                        <ErrorFormView errors={errors} field="code"/>
                    </div>
                    <button className={"w-100 btn btn-primary btn-spinner btn-lg" + (couponLoading ? ' loading' : '')}
                            type="button" id="cart-coupon" disabled={couponLoading || !couponValue} onClick={handleAddCoupon}>
                        <span className="left spinner-border spinner-border-sm"/>
                        {t("common.action.add")}
                    </button>
                </>
            )}
            {
                showVoucherProductsModal && <VoucherProductsModal show={showVoucherProductsModal}
                                                               onHide={() => setShowVoucherProductsModal(false)}
                                                               items={promotionItems}
                                                               promotions={promotions}
                                                               findMenuForPromotionItem={findMenuForPromotionItem}
                                                               cart={cart}
                                                               availabilities={availabilities}
                                                               session={session} handleRemovePromotion={handleRemovePromotion}
                />
            }
        </div>
    )
}

const mapStateToProps = (state: RootState) => ({
    cart: getCart(state.cart),
    items: getItems(state.menu),
    menus: getMenus(state.menu),
    categories: getCategories(state.menu),
    promotions: getPromotions(state.menu),
    session: getSession(state.session),
    availabilities: getAvailabilities(state.menu),
    vouchers: getVouchers(state.user),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(DrawerVouchersCoupon));